/**
 * RulesTab — Pricing rules rendered with one cell per parent column.
 * Uses shared SubTableRow/SubTableHeadRow components and centralized styling.
 */

import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { routes } from "@/lib/config/routes";
import type {
  ItemPricingRulesResponse,
  PricingRuleEntry,
} from "../../api/pricingSummaryHooks";
import {
  useTableStyles,
  SUB_TABLE_DATA_ROW_CLASS,
  SUB_TABLE_SECTION_HEADER_CLASS,
  SUB_TABLE_SKELETON_CLASS,
  SUB_TABLE_ACTION_BUTTON_CLASS,
} from "./useTableStyles";
import { SubTableRow, SubTableHeadRow } from "./SubTableRow";

const fmt = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatDiscount(rule: PricingRuleEntry): string {
  if (rule.rate_or_discount === "Discount Percentage" && rule.discount_percentage != null) {
    return `${rule.discount_percentage}% off`;
  }
  if (rule.rate_or_discount === "Discount Amount" && rule.discount_amount != null) {
    const prefix = rule.currency ?? "";
    return `${prefix} ${fmt.format(rule.discount_amount)} off`.trim();
  }
  if (rule.rate_or_discount === "Rate" && rule.rate != null) {
    const prefix = rule.currency ?? "";
    return `Rate: ${prefix} ${fmt.format(rule.rate)}`.trim();
  }
  return "—";
}

/** Map child fields to parent column indices. Skip first 3 system cols. */
function getFieldPositions(colCount: number) {
  const start = Math.min(3, colCount - 1);
  return {
    rule: start,
    discount: Math.min(start + 1, colCount - 1),
    minQty: Math.min(start + 2, colCount - 1),
    validFrom: Math.min(start + 3, colCount - 1),
    validTo: Math.min(start + 4, colCount - 1),
  };
}

function RuleSectionRows({
  title,
  rules,
  columnWidths,
  pos,
  cellClass,
  minHeight,
}: {
  title: string;
  rules: PricingRuleEntry[];
  columnWidths: number[];
  pos: ReturnType<typeof getFieldPositions>;
  cellClass: string;
  minHeight: number;
}) {
  if (rules.length === 0) return null;

  return (
    <>
      <div
        className={cn(cellClass, SUB_TABLE_SECTION_HEADER_CLASS)}
        style={{ minHeight }}
      >
        {title}
      </div>
      {rules.map((rule) => (
        <SubTableRow
          key={rule.name}
          columnWidths={columnWidths}
          cellClass={cellClass}
          minHeight={minHeight}
          cellExtraClass={{ [pos.minQty]: "text-right" }}
          className={SUB_TABLE_DATA_ROW_CLASS}
          cells={{
            [pos.rule]: <span className="truncate">{rule.title || rule.name}</span>,
            [pos.discount]: <span className="truncate">{formatDiscount(rule)}</span>,
            [pos.minQty]: <span className="tabular-nums">{rule.min_qty != null ? rule.min_qty : "—"}</span>,
            [pos.validFrom]: rule.valid_from ?? "—",
            [pos.validTo]: rule.valid_upto ?? "—",
          }}
        />
      ))}
    </>
  );
}

interface RulesTabProps {
  data: ItemPricingRulesResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  columnWidths: number[];
}

export function RulesTab({ data, isLoading, isError, refetch, columnWidths }: RulesTabProps) {
  const { headCellClass, dataCellClass, headMinHeight, dataMinHeight } = useTableStyles();
  const pos = getFieldPositions(columnWidths.length);

  const headerCells: Record<number, { label: string; align?: "right" }> = {
    [pos.rule]: { label: "Rule" },
    [pos.discount]: { label: "Discount / Rate" },
    [pos.minQty]: { label: "Min Qty", align: "right" },
    [pos.validFrom]: { label: "Valid From" },
    [pos.validTo]: { label: "Valid To" },
  };

  if (isLoading) {
    const skeleton = <div className={cn("h-3.5 w-16", SUB_TABLE_SKELETON_CLASS)} />;
    return (
      <div>
        <SubTableHeadRow columnWidths={columnWidths} cells={headerCells} headClass={headCellClass} minHeight={headMinHeight} />
        {[1, 2].map((i) => (
          <SubTableRow
            key={i}
            columnWidths={columnWidths}
            cellClass={dataCellClass}
            minHeight={dataMinHeight}
            className={SUB_TABLE_DATA_ROW_CLASS}
            cells={{
              [pos.rule]: <div className={cn("h-3.5 w-24", SUB_TABLE_SKELETON_CLASS)} />,
              [pos.discount]: skeleton,
              [pos.minQty]: skeleton,
              [pos.validFrom]: skeleton,
              [pos.validTo]: skeleton,
            }}
          />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className={cn(dataCellClass, "text-destructive")} style={{ minHeight: dataMinHeight }}>
        Could not load rules ·{" "}
        <button onClick={refetch} className="underline hover:no-underline">Retry</button>
      </div>
    );
  }

  const volumeBreaks = data?.volume_breaks ?? [];
  const customerContracts = data?.customer_contracts ?? [];
  const campaigns = data?.campaigns ?? [];

  if (volumeBreaks.length === 0 && customerContracts.length === 0 && campaigns.length === 0) {
    return (
      <div className={cn(dataCellClass, "text-muted-foreground")} style={{ minHeight: dataMinHeight }}>
        No active pricing rules ·{" "}
        <Link to={routes.pricingRule.new} className="underline hover:no-underline">
          New rule
        </Link>
      </div>
    );
  }

  return (
    <div>
      <SubTableHeadRow columnWidths={columnWidths} cells={headerCells} headClass={headCellClass} minHeight={headMinHeight} />
      <RuleSectionRows title="Volume Breaks" rules={volumeBreaks} columnWidths={columnWidths} pos={pos} cellClass={dataCellClass} minHeight={dataMinHeight} />
      <RuleSectionRows title="Customer Contracts" rules={customerContracts} columnWidths={columnWidths} pos={pos} cellClass={dataCellClass} minHeight={dataMinHeight} />
      <RuleSectionRows title="Campaigns" rules={campaigns} columnWidths={columnWidths} pos={pos} cellClass={dataCellClass} minHeight={dataMinHeight} />
      {/* Footer action zone */}
      <div className={cn(dataCellClass, "text-muted-foreground")} style={{ minHeight: dataMinHeight }}>
        <Link to={routes.pricingRule.new} className={SUB_TABLE_ACTION_BUTTON_CLASS}>
          <Plus className="h-3 w-3" /> New rule
        </Link>
      </div>
    </div>
  );
}
